import { useEffect, useState, useCallback } from "react";
import { Check, X, Loader2, Award, ChevronDown, ClipboardList } from "lucide-react";
import { UploadZone } from "@/components/mentor/UploadZone";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PracticeTab() {
  const [exercises, setExercises] = useState(null);
  const [open, setOpen] = useState(null);
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    fetch(`${API}/practice/exercises`, { credentials: "include" })
      .then((r) => r.json()).then(setExercises).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (ex) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/practice/exercises/${ex.id}/submit`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ image_path: image.path }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "Submission failed");
      setResult(d);
      if (d.xp_awarded > 0) toast.success(`Exercise passed! +${d.xp_awarded} XP`);
      load();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  if (!exercises) return <div className="py-16 text-center section-label text-[11px] text-zinc-600 animate-pulse">Loading exercises…</div>;

  return (
    <div className="space-y-3" data-testid="practice-tab">
      <div className="glass-card p-4 text-[12px] text-zinc-400 font-light">
        <span className="text-zinc-200 font-medium">Markup Practice — </span>
        Open any historical chart in TradingView, complete the tasks on it, screenshot it and submit. Chart Mentor grades your work. Pass with 6/10 or better to earn +30 XP.
      </div>
      {exercises.map((ex) => (
        <div key={ex.id} className="glass-card overflow-hidden" data-testid={`exercise-${ex.id}`}>
          <button onClick={() => { setOpen(open === ex.id ? null : ex.id); setImage(null); setResult(null); }}
            className="w-full p-4 flex items-center gap-4 text-left" data-testid={`exercise-open-${ex.id}`}>
            <span className={`h-9 w-9 shrink-0 rounded-full grid place-items-center border ${ex.passed ? "bg-[#7ee0a1] text-[#0A0A0A] border-transparent" : "border-white/15 text-zinc-400"}`}>
              {ex.passed ? <Check className="h-4 w-4" /> : <ClipboardList className="h-4 w-4" />}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-zinc-100 font-medium">{ex.title}</div>
              <div className="text-[12px] text-zinc-500 font-light truncate">{ex.desc}</div>
            </div>
            {ex.best_score != null && <span className="text-[11px] text-zinc-400">best {ex.best_score}/10</span>}
            <span className="section-label text-[8px] text-[#e6c67a]">+30 XP</span>
            <ChevronDown className={`h-4 w-4 text-zinc-600 transition-transform ${open === ex.id ? "rotate-180" : ""}`} />
          </button>
          {open === ex.id && (
            <div className="px-4 pb-5 border-t border-white/8 pt-4">
              <div className="section-label text-[9px] text-zinc-400 mb-2">Your tasks</div>
              <ol className="space-y-1.5 mb-4">
                {ex.tasks.map((t, i) => (
                  <li key={i} className="text-[13px] text-zinc-300 font-light flex gap-2">
                    <span className="text-[#9fd3ff]">{i + 1}.</span> {t}
                  </li>
                ))}
              </ol>
              {!result ? (
                <>
                  <UploadZone image={image} onUploaded={setImage} compact />
                  {image && (
                    <button onClick={() => submit(ex)} disabled={busy}
                      className="mt-3 btn-solid w-full py-3 text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
                      data-testid={`exercise-submit-${ex.id}`}>
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Award className="h-4 w-4" />}
                      {busy ? "Grading your markup…" : "Submit for grading"}
                    </button>
                  )}
                </>
              ) : (
                <div className="glass-card p-4" data-testid="exercise-result">
                  <div className="flex items-center justify-between">
                    <span className="section-label text-[10px] text-[#9fd3ff]">MBM Grade</span>
                    <span className={`font-heading text-2xl font-light ${result.passed ? "text-[#7ee0a1]" : "text-[#e6c67a]"}`}>{result.score}<span className="text-sm text-zinc-500">/10</span></span>
                  </div>
                  <div className="mt-3 space-y-1.5">
                    {result.task_checks.map((tc, i) => (
                      <div key={i} className="flex items-start gap-2 text-[12px]">
                        {tc.done ? <Check className="h-3.5 w-3.5 text-[#7ee0a1] mt-0.5 shrink-0" /> : <X className="h-3.5 w-3.5 text-[#ff7a7a] mt-0.5 shrink-0" />}
                        <span className="text-zinc-300 font-light">{tc.task}{tc.note ? <span className="text-zinc-500"> — {tc.note}</span> : null}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-[13px] text-zinc-300 font-light">{result.summary}</p>
                  <div className="mt-3 grid sm:grid-cols-2 gap-2 text-[12px]">
                    <div className="rounded-lg border border-[#7ee0a1]/20 bg-[#7ee0a1]/5 p-2.5 text-zinc-300"><span className="text-[#7ee0a1]">Well done: </span>{result.did_well}</div>
                    <div className="rounded-lg border border-[#e6c67a]/20 bg-[#e6c67a]/5 p-2.5 text-zinc-300"><span className="text-[#e6c67a]">Improve: </span>{result.improve}</div>
                  </div>
                  <button onClick={() => { setResult(null); setImage(null); }} className="mt-3 btn-glass text-white px-4 py-2 text-xs">Try again</button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
